import React, { FC, useContext, useEffect } from 'react';
import _ from 'lodash';
import { UserContext } from 'contexts/ConfigContext';
import { formatNumWithKMBAlt, getConfig } from 'utils/helperMethods';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import useTrackTabChange from 'hooks/general/useTrackTabChange';

if (typeof window !== 'undefined') {
    var G2 = require('@antv/g2');
    var { Chart } = G2;
}
interface Props {
    title?: string;
    dataset: any;
    chartContainer?: string;
    height?: number;
    width?: number;
    fields?: Array<string>;
    scrollBarWidth?: number;
    barWidth?: string;
    check?: boolean;
    xAxisHide?: boolean;
    legendConfig?: boolean;
    colorScheme?: Array<string>;
    labelFill?: string;
    error?: any;
    loading?: boolean;
    selectedGraphItem?: any;
    onClick?: Function;
    xTitle: string;
    tooltipPosition?: 'left' | 'right' | 'top' | 'bottom';
    decimalForPercentage: number;
    decimalForNonPercentage: number;
}
const PricePointBarChart: FC<Props> = ({
    dataset,
    chartContainer,
    height,
    width,
    fields = ['attributeType', 'price', 'value'],
    scrollBarWidth = 3,
    barWidth = '1000',
    check = true,
    xAxisHide = false,
    legendConfig = false,
    colorScheme = [],
    error,
    loading = false,
    onClick,
    selectedGraphItem,
    xTitle = '',
    tooltipPosition = 'left',
    decimalForNonPercentage,
    decimalForPercentage,
}) => {
    const isWindowTabActive = useTrackTabChange();
    const { appConfig } = useContext(UserContext);
    const commonSetting = getConfig('commonConfig', appConfig);
    const { graphFontSize, graphLabelColor, graphFontSizeAlt, graphLabelColorAlt, graphFontFamily } = commonSetting;
    if (!loading && error && dataset?.length === 0) {
        return <ErrorBox msg={String(error)} />;
    }

    useEffect(() => {
        if (!window) return;
        if (!dataset) return;
        // Reimplements @antv/data-set's `percent` transform (src/transform/percent.ts)
        // for this chart. Buckets rows by `price`, sub-buckets by `attributeType`;
        // each output row carries the (price, attributeType)-level sum of `value` and
        // its share within the price bucket. The `_${...}` key prefix mirrors the
        // upstream `partition` util and preserves input-order iteration when the
        // grouping value is numeric (integer-like Object keys otherwise sort numerically).
        // Replaces @antv/data-set to eliminate its bundled lodash 4.17.20 copy from the
        // production bundle.
        const dimensionKey = fields[0];
        const valueKey = fields[2];
        const inputRows: any[] = Array.isArray(dataset) ? dataset : [];
        const percentRows: any[] = [];
        const priceBuckets = _.groupBy(inputRows, (r) => `_${r?.price}`);
        Object.values(priceBuckets).forEach((bucket) => {
            const totalSum = bucket.reduce((s, r) => s + Number(r?.[valueKey] ?? 0), 0);
            const dimensionBuckets = _.groupBy(bucket, (r) => `_${r?.[dimensionKey]}`);
            Object.values(dimensionBuckets).forEach((innerBucket) => {
                const innerSum = innerBucket.reduce((s, r) => s + Number(r?.[valueKey] ?? 0), 0);
                percentRows.push({
                    ...innerBucket[0],
                    [valueKey]: innerSum,
                    percent: totalSum === 0 ? 0 : innerSum / totalSum,
                });
            });
        });

        const chart = new Chart({
            container: chartContainer,
            width: width,
            height: height,
        });

        chart.data(percentRows);
        chart.scale({
            percent: {
                min: 0,
                formatter(val: any) {
                    return (val * 100).toFixed(2) + '%';
                },
            },
        });

        chart.axis(fields[1], {
            style: {
                fontSize: graphFontSize,
                fontFamily: graphFontFamily,
                fill: graphLabelColor,
            },
            title: null,
            label: null,
        });
        chart.axis('percent', {
            style: {
                fontSize: graphFontSize,
                fontFamily: graphFontFamily,
                fill: graphLabelColor,
            },
            label: {
                style: {
                    fontSize: graphFontSize,
                    fontFamily: graphFontFamily,
                    fill: graphLabelColor,
                },
            },
        });
        chart.tooltip({
            showMarkers: true,
            position: tooltipPosition,
        });

        if (legendConfig) {
            chart.legend(fields[1], {
                position: 'top',
            });
        } else {
            chart.legend(fields[0], false);
            chart.legend(fields[1], false);
        }
        chart.legend(false);
        if (check) chart.axis('percent', false);

        if (xAxisHide) chart.axis(fields[1], false);

        chart
            .interval()
            .position(`${fields[1]}*percent`)
            .color('color', (val: any) => {
                return val;
            })
            // .style(`${fields[0]}*${fields[1]}`, (xAxis: any, yAxis: any) => {
            //     if (onClick && selectedGraphItem?.attributeName === xAxis && yAxis === selectedGraphItem?.price)
            //         return {
            //             fill: selectedGraphItem?.color,
            //             cursor: 'pointer',
            //         };
            //     return {
            //         cursor: 'pointer',
            //     };
            // })
            .size(fields[1], [barWidth])
            .adjust('stack')
            .tooltip(`attributeType*price*value*range`, (attributeType: any, price: any, value: any, range: any) => {
                const rangeLabel = `${commonSetting.units.currency}${
                    formatNumWithKMBAlt(range?.[0], decimalForNonPercentage, decimalForNonPercentage) ?? ''
                } - ${commonSetting.units.currency}${
                    formatNumWithKMBAlt(range?.[1], decimalForNonPercentage, decimalForNonPercentage) ?? ''
                }`;
                return {
                    name: attributeType,
                    value: formatNumWithKMBAlt(value, decimalForPercentage) + '%',
                    title: rangeLabel,
                };
            });
        chart.on('element:click', (...args: any) => {
            const data = args?.[0]?.data?.data;
            // if (onClick) {
            //     //resetting logic
            //     if (
            //         selectedGraphItem?.attributeName === data?.attributeType &&
            //         data?.price === selectedGraphItem?.price
            //     )
            //         onClick(null);
            //     else onClick({ attributeName: data?.attributeType, price: data?.price, color: '#44338A' });
            // }
        });
        //   chart.interaction('active-region');

        if (window) {
            try {
                chart.render();
            } catch (e) {
                console.log('This is chart render block error', e);
            }
        }
        return () => {
            chart.destroy();
        };
    }, [dataset, width, isWindowTabActive]);
    return <div id={chartContainer} className={``} style={{}}></div>;
};

function arePropsEqual(prevProps: any, nextProps: any) {
    return _.isEqual(prevProps, nextProps);
}

export default React.memo(PricePointBarChart, arePropsEqual);
